/**
 * CartContext.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { createContext, useReducer, useEffect, useCallback } from 'react';
import { getWholesalePrice } from '../data/products';
import { getWholesalePricing } from '../utils/wholesalePricing';
import { calculateShipping, calculateCartWeight, isBillableForShipping, calculateTransactionFeeCents } from '../utils/shippingConfig';
import { calculateTax } from '../utils/taxConfig';
import { useAuth } from './AuthContext';
import { getLineUnitPriceCents } from '../utils/cartPricing';
import { getVariantAdjustedPriceCents, getVariantPriceAdjustmentCents } from '../utils/variantPricing';

export const CartContext = createContext();

const PRODUCTS_URL = '/api/products';

let catalogPromise = null;

const findProductById = (products, id) =>
  products.find((p) => p.id === id || String(p.id) === String(id));

/** Register a product from a detail-page payload when the cached catalog is stale (e.g. newly created). */
const resolveCatalogProduct = (products, item) => {
  const found = findProductById(products, item.id);
  if (found) return { product: found, products };

  if (item?.id != null && (item.price_cents != null || item.price != null)) {
    const registered = {
      ...item,
      inventory: Number(item.inventory) || 0,
      price_cents: item.price_cents ?? item.price ?? 0,
    };
    return { product: registered, products: [...products, registered] };
  }

  return { product: null, products };
};

const fetchProductsCatalog = async ({ force = false } = {}) => {
  if (force) catalogPromise = null;
  if (!catalogPromise) {
    catalogPromise = fetch(PRODUCTS_URL)
      .then((productsResponse) => {
        if (!productsResponse.ok) {
          throw new Error('Failed to fetch products');
        }
        return productsResponse.json();
      })
      .catch((err) => {
        catalogPromise = null;
        throw err;
      });
  }
  return catalogPromise;
};

// Helper function to recalculate wholesale pricing for an item
const recalculateWholesalePricing = (item, product, currentUser) => {
  if (item?.isBundle) {
    const bundlePrice = item.unitPrice ?? item.price_cents ?? item.price ?? 0;
    return {
      unitPrice: bundlePrice,
      price_cents: item.price_cents ?? bundlePrice,
      variantPriceAdjustmentCents: item.variantPriceAdjustmentCents ?? 0,
      isWholesalePrice: false,
      wholesaleTier: null
    };
  }

  const variantPriceAdjustmentCents = getVariantPriceAdjustmentCents(product, item?.selectedSize, item?.selectedColor);
  const retailPrice = getVariantAdjustedPriceCents(product, item?.selectedSize, item?.selectedColor);

  if (!currentUser?.isWholesaler || !getWholesalePricing(product)) {
    return {
      unitPrice: retailPrice,
      price_cents: retailPrice,
      variantPriceAdjustmentCents,
      isWholesalePrice: false,
      wholesaleTier: null
    };
  }

  const wholesaleTier = currentUser.wholesaleTier || 'standard';
  const baseWholesalePrice = Number(getWholesalePrice(product, wholesaleTier, item.quantity));
  const wholesalePrice = (Number.isFinite(baseWholesalePrice) ? baseWholesalePrice : retailPrice - variantPriceAdjustmentCents) + variantPriceAdjustmentCents;
  const isWholesalePrice = wholesalePrice !== retailPrice;

  return {
    unitPrice: wholesalePrice,
    price_cents: retailPrice,
    variantPriceAdjustmentCents,
    isWholesalePrice,
    wholesaleTier: isWholesalePrice ? wholesaleTier : null
  };
};

const cartReducer = (state, action) => {
  // console.log('🛒 CartReducer: Action dispatched:', action.type, action.payload); // Commented out to reduce console noise

  switch (action.type) {
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload };

    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };

    case 'SET_TAX_SETTINGS':
      return { ...state, taxSettings: action.payload };

    case 'ADD_ITEM': {
      const { item, quantity = 1 } = action.payload;
      const { product, products: catalogProducts } = resolveCatalogProduct(state.products, item);

      // Don't add if product not found or insufficient inventory (skip inventory check for bundles)
      if (!product || (!item.isBundle && product.inventory < quantity)) return state;

      if (item.isCheckoutPromo && state.items.some((i) => i.id === item.id && i.isCheckoutPromo)) {
        return state;
      }

      const updatedProducts = item.isBundle
        ? catalogProducts // Don't modify inventory for bundles
        : catalogProducts.map((p) =>
          (p.id === item.id || String(p.id) === String(item.id))
            ? { ...p, inventory: p.inventory - quantity }
            : p
        );

      // Generate a unique cartItemId if not provided, using timestamp + random to ensure uniqueness
      const cartItemId = item.cartItemId || `${item.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // For non-bundle items, check if we can merge with existing item (same id, size, color)
      const existingItem = !item.isBundle && state.items.find((i) =>
        i.id === item.id &&
        i.selectedSize === item.selectedSize &&
        i.selectedColor === item.selectedColor &&
        !i.isBundle &&
        !!i.isCheckoutPromo === !!item.isCheckoutPromo
      );

      if (existingItem) {
        // Only allow adding to existing cart item if it's not a bundle and variants match
        const newQuantity = existingItem.quantity + quantity;
        const updatedItem = { ...existingItem, quantity: newQuantity };

        // Recalculate wholesale pricing with new quantity (checkout promos stay $0)
        const pricingInfo = existingItem.isCheckoutPromo
          ? { unitPrice: 0, isWholesalePrice: false, wholesaleTier: null }
          : recalculateWholesalePricing(updatedItem, product, state.currentUser);

        const updatedItems = state.items.map((i) =>
          (i.cartItemId || i.id) === (existingItem.cartItemId || existingItem.id) ? {
            ...updatedItem,
            ...pricingInfo,
            price_cents: existingItem.isCheckoutPromo ? 0 : updatedItem.price_cents
          } : i
        );
        return { ...state, items: updatedItems, products: updatedProducts };
      }

      // For bundles, use the pre-calculated price; for regular items, apply wholesale pricing
      let finalItem;
      if (item.isBundle) {
        // Bundle: use pre-calculated discounted price
        finalItem = {
          ...item,
          cartItemId: cartItemId,
          quantity: quantity,
          // Keep the bundle's calculated price
          unitPrice: item.unitPrice, // This is already the discounted total
          price_cents: item.unitPrice, // Ensure price_cents matches
          originalPrice: item.originalPrice, // Store original price before discount
          bundleDiscount: item.bundleDiscount, // Store discount percentage
          bundleProducts: item.bundleProducts, // Store selected products
          isBundle: true,
          isWholesalePrice: false,
          wholesaleTier: null
        };
      } else if (item.isCheckoutPromo) {
        finalItem = {
          ...item,
          cartItemId,
          quantity,
          unitPrice: 0,
          price_cents: 0,
          isCheckoutPromo: true,
          isWholesalePrice: false,
          wholesaleTier: null
        };
      } else {
        // Regular item: apply wholesale pricing if applicable
        const newItem = {
          ...item,
          cartItemId: cartItemId,
          quantity: quantity,
          unitPrice: item.unitPrice ?? item.price_cents ?? item.price ?? 0,
          isWholesalePrice: item.isWholesalePrice || false,
          wholesaleTier: item.wholesaleTier || null
        };

        // Recalculate wholesale pricing for new item
        const pricingInfo = recalculateWholesalePricing(newItem, product, state.currentUser);
        finalItem = { ...newItem, ...pricingInfo };
      }

      const updatedItems = [...state.items, finalItem];
      return { ...state, items: updatedItems, products: updatedProducts };
    }
    case 'REMOVE_CHECKOUT_PROMOS': {
      const promoItems = state.items.filter((i) => i.isCheckoutPromo);
      if (promoItems.length === 0) return state;

      let updatedProducts = state.products;
      for (const itemToRemove of promoItems) {
        const productRow = updatedProducts.find((p) => p.id === itemToRemove.id);
        if (productRow) {
          updatedProducts = updatedProducts.map((p) =>
            p.id === itemToRemove.id
              ? { ...p, inventory: p.inventory + itemToRemove.quantity }
              : p
          );
        }
      }
      const updatedItems = state.items.filter((i) => !i.isCheckoutPromo);
      return {
        ...state,
        items: updatedItems,
        products: updatedProducts,
        dismissedCheckoutPromoIds: []
      };
    }
    case 'REMOVE_ITEM': {
      const { id } = action.payload; // This is the cartItemId
      const itemToRemove = state.items.find((i) => (i.cartItemId || i.id) === id);

      // We need the product ID to update inventory
      if (!itemToRemove) return state;
      const productId = itemToRemove.id;
      const product = state.products.find((p) => p.id === productId);

      if (!product) return state;

      const updatedProducts = state.products.map((p) =>
        p.id === productId ? { ...p, inventory: p.inventory + itemToRemove.quantity } : p
      );
      const updatedItems = state.items.filter((i) => (i.cartItemId || i.id) !== id);

      const dismissedCheckoutPromoIds = itemToRemove.isCheckoutPromo
        ? [...new Set([...(state.dismissedCheckoutPromoIds || []), productId])]
        : state.dismissedCheckoutPromoIds || [];

      return { ...state, items: updatedItems, products: updatedProducts, dismissedCheckoutPromoIds };
    }
    case 'UPDATE_QUANTITY': {
      const { id, quantity } = action.payload; // This is the cartItemId
      const itemToUpdate = state.items.find((i) => (i.cartItemId || i.id) === id);

      if (!itemToUpdate) return state;
      const productId = itemToUpdate.id;
      const product = state.products.find((p) => p.id === productId);

      if (!product) return state;

      const quantityChange = quantity - itemToUpdate.quantity;

      // Don't allow quantity to exceed available inventory
      if (quantityChange > product.inventory) return state;

      const updatedProducts = state.products.map((p) =>
        p.id === productId ? { ...p, inventory: p.inventory - quantityChange } : p
      );

      // Update item with new quantity and recalculate wholesale pricing
      const updatedItem = { ...itemToUpdate, quantity };
      const pricingInfo = itemToUpdate.isCheckoutPromo
        ? { unitPrice: 0, isWholesalePrice: false, wholesaleTier: null }
        : recalculateWholesalePricing(updatedItem, product, state.currentUser);

      const updatedItems = state.items.map((i) =>
        (i.cartItemId || i.id) === id ? {
          ...updatedItem,
          ...pricingInfo,
          price_cents: itemToUpdate.isCheckoutPromo ? 0 : updatedItem.price_cents
        } : i
      ).filter(item => item.quantity > 0); // Remove if quantity is 0 or less

      const dismissedCheckoutPromoIds =
        itemToUpdate.isCheckoutPromo && quantity <= 0
          ? [...new Set([...(state.dismissedCheckoutPromoIds || []), productId])]
          : state.dismissedCheckoutPromoIds || [];

      return { ...state, items: updatedItems, products: updatedProducts, dismissedCheckoutPromoIds };
    }
    case 'UPDATE_ITEM_PRICING': {
      const { id, unitPrice, isWholesalePrice, wholesaleTier } = action.payload;
      const updatedItems = state.items.map((i) =>
        i.id === id ? {
          ...i,
          unitPrice,
          isWholesalePrice,
          wholesaleTier
        } : i
      );
      return { ...state, items: updatedItems };
    }
    case 'RECALCULATE_WHOLESALE_PRICING': {
      // Recalculate wholesale pricing for all items when user changes or login status changes
      const updatedItems = state.items.map((item) => {
        if (item.isCheckoutPromo) {
          return { ...item, unitPrice: 0, price_cents: 0, isWholesalePrice: false, wholesaleTier: null };
        }
        const product = state.products.find((p) => p.id === item.id);
        if (!product) return item;

        const pricingInfo = recalculateWholesalePricing(item, product, state.currentUser);
        return { ...item, ...pricingInfo };
      });
      return { ...state, items: updatedItems };
    }
    case 'CLEAR_CART': {
      const updatedProducts = state.products.map(p => {
        const itemInCart = state.items.find(i => i.id === p.id);
        if (itemInCart && p.inventory !== undefined) {
          return { ...p, inventory: p.inventory + itemInCart.quantity };
        }
        return p;
      });
      return { ...state, items: [], products: updatedProducts, dismissedCheckoutPromoIds: [] };
    }
    case 'LOAD_CART': {
      // Filter out items that don't have corresponding products and recalculate pricing
      const validItems = action.payload.items
        .filter(item => findProductById(state.products, item.id))
        .map(item => {
          const product = findProductById(state.products, item.id);
          if (!product) return item;

          if (item.isCheckoutPromo) {
            return {
              ...item,
              unitPrice: 0,
              price_cents: 0,
              isWholesalePrice: false,
              wholesaleTier: null
            };
          }

          // Ensure item has correct pricing information
          const itemWithPricing = {
            ...item,
            unitPrice: item.unitPrice ?? item.price_cents ?? item.price ?? product.price_cents ?? product.price ?? 0,
            isWholesalePrice: item.isWholesalePrice || false,
            wholesaleTier: item.wholesaleTier || null
          };

          // Recalculate wholesale pricing
          const pricingInfo = recalculateWholesalePricing(itemWithPricing, product, state.currentUser);
          return { ...itemWithPricing, ...pricingInfo };
        });
      return {
        ...state,
        items: validItems,
        dismissedCheckoutPromoIds: action.payload.dismissedCheckoutPromoIds || []
      };
    }
    case 'UPDATE_SHIPPING': {
      const { method, country, stateProvince, shippingInfo: preCalculatedShipping } = action.payload;

      // No postage when every line is digital, a checkout promo, or shipping-disabled
      const allItemsVirtual = state.items.length > 0 && state.items.every((item) => !isBillableForShipping(item));

      // If all items are virtual, set shipping to 0
      if (allItemsVirtual) {
        return {
          ...state,
          shipping: {
            method: 'none',
            cost: 0,
            country,
            stateProvince,
            isCalculated: true,
            info: {
              cost: 0,
              method: { id: 'none', name: 'No Shipping Required', description: 'Digital Products' },
              isFree: true,
              message: 'No shipping required for digital products'
            }
          }
        };
      }

      // Use pre-calculated shipping info from async updateShipping call
      const shippingInfo = preCalculatedShipping || { cost: 0, isFree: false, message: null };

      return {
        ...state,
        shipping: {
          method,
          cost: shippingInfo.cost,
          country,
          stateProvince,
          isCalculated: true,
          info: shippingInfo,
          transactionFeePct: shippingInfo.transactionFeePct || 0,
        }
      };
    }
    case 'UPDATE_TAX': {
      const { country, stateProvince } = action.payload;
      const cartTotal = state.items.reduce((total, item) => {
        const itemPrice = getLineUnitPriceCents(item);
        return total + (itemPrice * item.quantity);
      }, 0);

      const taxInfo = calculateTax(
        cartTotal,
        country,
        stateProvince,
        state.items,
        state.taxSettings,
        state.shipping.cost
      );

      return {
        ...state,
        tax: {
          amount: taxInfo.tax,
          rate: taxInfo.rate,
          breakdown: taxInfo.breakdown,
          isCalculated: true
        }
      };
    }
    case 'UPDATE_TARIFF': {
      const { tariffAmount, isTariffApplicable, exemptionThreshold, tariffRate, message, tariffInfo } = action.payload;
      return {
        ...state,
        tariff: {
          amount: tariffAmount || 0,
          isApplicable: isTariffApplicable || false,
          exemptionThreshold: exemptionThreshold || 0,
          rate: tariffRate || 0,
          message: message || '',
          isCalculated: true,
          ...tariffInfo
        }
      };
    }
    case 'RECALCULATE_SHIPPING_TAX': {
      // Note: This is called synchronously from the reducer,
      // so it will use hardcoded shipping values as fallback
      // For accurate rates, use updateShipping() which is async
      const cartTotal = state.items.reduce((total, item) => {
        const itemPrice = getLineUnitPriceCents(item);
        return total + (itemPrice * item.quantity);
      }, 0);

      const totalWeightKg = calculateCartWeight(state.items);

      // This will return a Promise, but we can't await in a reducer
      // So we'll use hardcoded fallback values here
      const shippingInfo = { cost: 0, isFree: false, message: 'Calculating...' };
      const taxInfo = calculateTax(
        cartTotal,
        state.shipping.country,
        state.shipping.stateProvince,
        state.items,
        state.taxSettings,
        state.shipping.cost
      );

      // calculateShipping expects weight in kg (same as updateShipping)
      calculateShipping(cartTotal, totalWeightKg, state.shipping.country, state.shipping.method, state.items)
        .then(info => {
          // This is a hack - we can't dispatch from inside a reducer
          // The updateShipping should be called instead
          // Shipping is applied via updateShipping(); reducer cannot dispatch.
        });

      return {
        ...state,
        shipping: {
          ...state.shipping,
          cost: shippingInfo.cost,
          isCalculated: false, // Mark as not calculated since we're using placeholder
          info: shippingInfo
        },
        tax: {
          amount: taxInfo.tax,
          rate: taxInfo.rate,
          breakdown: taxInfo.breakdown,
          isCalculated: true,
          hasBooks: taxInfo.hasBooks,
          isInternational: taxInfo.isInternational
        }
      };
    }
    default:
      return state;
  }
};

const initialState = {
  items: [],
  products: [], // Products will be loaded from fetch
  currentUser: null, // Current user for wholesale pricing calculations
  shipping: {
    method: 'standard',
    cost: 0,
    country: 'US',
    stateProvince: null,
    isCalculated: false
  },
  tax: {
    amount: 0,
    rate: 0,
    breakdown: [],
    isCalculated: false
  },
  tariff: {
    amount: 0,
    isApplicable: false,
    exemptionThreshold: 0,
    rate: 0,
    message: '',
    isCalculated: false
  },
  taxSettings: [],
  dismissedCheckoutPromoIds: []
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { currentUser } = useAuth();

  useEffect(() => {
    // Fetch products and then load cart from localStorage
    const fetchData = async () => {
      try {
        const productsData = await fetchProductsCatalog();
        dispatch({ type: 'SET_PRODUCTS', payload: productsData });

        const savedCart = localStorage.getItem('shoppingCart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          dispatch({
            type: 'LOAD_CART',
            payload: {
              items: parsedCart.items || [],
              dismissedCheckoutPromoIds: parsedCart.dismissedCheckoutPromoIds || []
            }
          });
        }

        // Fetch tax settings
        const taxResponse = await fetch('/api/tax-settings');
        if (taxResponse.ok) {
          const taxData = await taxResponse.json();
          dispatch({ type: 'SET_TAX_SETTINGS', payload: taxData });
        }
      } catch (error) {
        console.error("Failed to fetch products or load cart:", error);
      }
    };
    fetchData();
  }, []);

  const refreshProducts = useCallback(async () => {
    try {
      const productsData = await fetchProductsCatalog({ force: true });
      dispatch({ type: 'SET_PRODUCTS', payload: productsData });
      return productsData;
    } catch (error) {
      console.error('Failed to refresh product catalog:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (state.products.length > 0) {
      localStorage.setItem('shoppingCart', JSON.stringify({
        items: state.items,
        dismissedCheckoutPromoIds: state.dismissedCheckoutPromoIds || []
      }));
    }
  }, [state.items, state.products, state.dismissedCheckoutPromoIds]);

  // Auto-add checkout promotional gifts ($0) only when the cart has physical (shippable) paid items.
  // Digital-only carts (downloads, etc.) must not get free physical gifts like coasters.
  useEffect(() => {
    if (state.products.length === 0) return;

    const paidItems = state.items.filter((i) => !i.isCheckoutPromo);
    const resolveCartLine = (item) => {
      const product = findProductById(state.products, item.id);
      return product ? { ...product, ...item } : item;
    };
    const hasPhysicalPaidItem = paidItems.some((item) =>
      isBillableForShipping(resolveCartLine(item))
    );

    if (paidItems.length === 0 || !hasPhysicalPaidItem) {
      if (state.items.some((i) => i.isCheckoutPromo)) {
        dispatch({ type: 'REMOVE_CHECKOUT_PROMOS' });
      }
      return;
    }

    const isCheckoutPromoProduct = (p) =>
      p.checkout_promo === 1 ||
      p.checkout_promo === true ||
      p.checkout_promo === '1';

    const promoCandidates = state.products.filter((p) => {
      if (!isCheckoutPromoProduct(p)) return false;
      if (p.is_bundle === 1 || p.is_bundle === true) return false;
      if (p.has_variants === 1 || p.has_variants === true) return false;
      if ((p.inventory || 0) <= 0) return false;
      return true;
    });

    const dismissed = state.dismissedCheckoutPromoIds || [];

    for (const p of promoCandidates) {
      if (dismissed.includes(p.id)) continue;
      if (state.items.some((i) => i.id === p.id)) continue;
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          item: {
            ...p,
            unitPrice: 0,
            price_cents: 0,
            isCheckoutPromo: true,
            isWholesalePrice: false,
            wholesaleTier: null
          },
          quantity: 1
        }
      });
    }
  }, [state.items, state.products, state.dismissedCheckoutPromoIds]);

  // Recalculate pricing when user changes
  useEffect(() => {
    if (state.items.length > 0 && state.products.length > 0 && currentUser) {
      dispatch({ type: 'RECALCULATE_WHOLESALE_PRICING' });
    }
  }, [currentUser, state.items.length, state.products.length]);

  const addItem = (item, quantity = 1) => {
    const { product } = resolveCatalogProduct(state.products, item);
    const isDigitalAlbum =
      product &&
      (product.is_digital_album === 1 ||
        product.is_digital_album === true ||
        product.is_digital_album === '1');
    if (!product || (!item.isBundle && !isDigitalAlbum && product.inventory < quantity)) {
      return false;
    }
    dispatch({ type: 'ADD_ITEM', payload: { item, quantity } });
    return true;
  };

  const removeItem = (id) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { id } });
  };

  const updateQuantity = (id, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const updateItemPricing = (id, unitPrice, isWholesalePrice, wholesaleTier) => {
    dispatch({ type: 'UPDATE_ITEM_PRICING', payload: { id, unitPrice, isWholesalePrice, wholesaleTier } });
  };

  const setCurrentUser = (user) => {
    dispatch({ type: 'SET_CURRENT_USER', payload: user });
    // Recalculate wholesale pricing when user changes
    dispatch({ type: 'RECALCULATE_WHOLESALE_PRICING' });
  };

  // Update current user in state when AuthContext changes
  useEffect(() => {
    dispatch({ type: 'SET_CURRENT_USER', payload: currentUser });
  }, [currentUser]);

  const recalculateWholesalePricing = () => {
    dispatch({ type: 'RECALCULATE_WHOLESALE_PRICING' });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  // Calculate total with wholesale pricing
  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      const itemPrice = getLineUnitPriceCents(item);
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  // Get wholesale savings
  const getWholesaleSavings = () => {
    return state.items.reduce((savings, item) => {
      if (item.isWholesalePrice) {
        const retailPrice = item.price_cents ?? item.price ?? 0;
        const wholesalePrice = item.unitPrice ?? 0;
        const itemSavings = (retailPrice - wholesalePrice) * item.quantity;
        return savings + itemSavings;
      }
      return savings;
    }, 0);
  };

  // Shipping and tax functions
  const updateShipping = useCallback(async (method, country, stateProvince = null) => {
    const cartTotal = state.items.reduce((total, item) => {
      const itemPrice = getLineUnitPriceCents(item);
      return total + (itemPrice * item.quantity);
    }, 0);

    const totalWeight = calculateCartWeight(state.items);

    try {
      const shippingInfo = await calculateShipping(cartTotal, totalWeight, country, method, state.items);
      dispatch({ type: 'UPDATE_SHIPPING', payload: { method, country, stateProvince, shippingInfo } });
    } catch (error) {
      console.error('Error calculating shipping:', error);
      dispatch({ type: 'UPDATE_SHIPPING', payload: { method, country, stateProvince } });
    }
  }, [state.items]);

  const updateTax = (country, stateProvince = null) => {
    dispatch({ type: 'UPDATE_TAX', payload: { country, stateProvince } });
  };

  const updateTariff = async (country, countryCode) => {
    try {
      const subtotalCents = state.items.reduce((total, item) => {
        const itemPrice = getLineUnitPriceCents(item);
        return total + (itemPrice * item.quantity);
      }, 0);

      const response = await fetch('/api/calculate-tariff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subtotalCents,
          country,
          countryCode
        })
      });

      if (response.ok) {
        const tariffInfo = await response.json();
        dispatch({
          type: 'UPDATE_TARIFF',
          payload: {
            tariffAmount: tariffInfo.tariffAmount,
            isTariffApplicable: tariffInfo.isTariffApplicable,
            exemptionThreshold: tariffInfo.exemptionThreshold,
            tariffRate: tariffInfo.tariffRate,
            message: tariffInfo.message,
            tariffInfo
          }
        });
      } else {
        console.error('Failed to calculate tariff');
        // Reset tariff on error
        dispatch({
          type: 'UPDATE_TARIFF',
          payload: {
            tariffAmount: 0,
            isTariffApplicable: false,
            exemptionThreshold: 0,
            tariffRate: 0,
            message: ''
          }
        });
      }
    } catch (error) {
      console.error('Error calculating tariff:', error);
      // Reset tariff on error
      dispatch({
        type: 'UPDATE_TARIFF',
        payload: {
          tariffAmount: 0,
          isTariffApplicable: false,
          exemptionThreshold: 0,
          tariffRate: 0,
          message: ''
        }
      });
    }
  };

  const recalculateShippingAndTax = () => {
    dispatch({ type: 'RECALCULATE_SHIPPING_TAX' });
  };

  // Get total with shipping, tax, tariff, and optional payment processing fee
  const getPreTransactionFeeTotal = () => {
    const subtotal = getCartTotal();
    const shippingCost = state.shipping.cost || 0;
    const tax = state.tax.amount || 0;
    const tariff = state.tariff.amount || 0;
    return subtotal + shippingCost + tax + tariff;
  };

  const getTransactionFee = () => {
    const pct = state.shipping.transactionFeePct || state.shipping.info?.transactionFeePct || 0;
    const preFeeTotal = getPreTransactionFeeTotal();
    const amount = calculateTransactionFeeCents(preFeeTotal, pct);
    return { amount, pct, preFeeTotal };
  };

  const getTotalWithShippingAndTax = () => {
    const preFeeTotal = getPreTransactionFeeTotal();
    const { amount: transactionFee } = getTransactionFee();
    return preFeeTotal + transactionFee;
  };

  // Get available shipping methods
  const getAvailableShippingMethods = () => {
    return getAvailableShippingMethods(state.shipping.country);
  };

  const cart = {
    ...state,
    addItem,
    removeItem,
    updateQuantity,
    updateItemPricing,
    setCurrentUser,
    recalculateWholesalePricing,
    clearCart,
    getCartTotal,
    getWholesaleSavings,
    updateShipping,
    updateTax,
    updateTariff,
    recalculateShippingAndTax,
    getTotalWithShippingAndTax,
    getPreTransactionFeeTotal,
    getTransactionFee,
    getAvailableShippingMethods,
    refreshProducts,
  };

  return <CartContext.Provider value={cart}>{children}</CartContext.Provider>
}; 